import {
  Alert,
  Box,
  Button,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ApiRequestError, publicRequestJson } from '../api';
import { LutiqLogo } from '../components/common/LutiqLogo';

type InviteStatus = {
  email: string | null;
  actionType: string;
  expiresAt: string;
  // Absent on responses from a backend older than the auto-resend feature;
  // treat missing as valid so a deploy-skew window can't lock the form.
  state?: 'valid' | 'expired';
  resent?: boolean;
};

const expiredMessage = (invite: InviteStatus | null): string => {
  if (invite?.actionType === 'password_reset') {
    return 'This link has expired. Please request a new one from the sign-in page.';
  }
  if (invite?.resent) {
    return 'This link has expired. We just emailed you a new one — please check your inbox.';
  }
  return 'This link has expired. Please check your email for a new link.';
};

const AcceptInvitePage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error' | 'expired'>('idle');
  const [invite, setInvite] = useState<InviteStatus | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('Invite token is missing.');
      return;
    }
    setStatus('loading');
    void publicRequestJson<InviteStatus>(`/api/auth/invites/${encodeURIComponent(token)}`)
      .then((payload) => {
        setInvite(payload);
        setStatus(payload.state === 'expired' ? 'expired' : 'idle');
      })
      .catch((error) => {
        setStatus('error');
        setMessage(error instanceof Error ? error.message : 'Invite is invalid.');
      });
  }, [token]);

  const submitLabel = invite?.actionType === 'password_reset' ? 'Reset password' : 'Set password';
  const requiresName = invite?.actionType !== 'password_reset';

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatus('loading');
    setMessage(null);
    try {
      await publicRequestJson('/api/auth/invites/accept', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          password,
          ...(requiresName ? { firstName, lastName } : {}),
        }),
      });
      setStatus('success');
    } catch (error) {
      // 410 Gone = the link expired between page load and submit. The
      // backend has already fired its guarded auto-resend, so show the
      // same check-your-email guidance instead of a raw error.
      if (error instanceof ApiRequestError && error.status === 410) {
        setStatus('expired');
        return;
      }
      setStatus('error');
      setMessage(error instanceof Error ? error.message : 'Unable to complete invite.');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        bgcolor: 'background.default',
      }}
    >
      <Paper elevation={0} sx={{ p: { xs: 3, md: 5 }, maxWidth: 520, width: '100%' }}>
        <Stack spacing={3}>
          <LutiqLogo height={28} />
          <Box>
            <Typography variant="overline" color="primary.main" sx={{ fontWeight: 700 }}>
              Account setup
            </Typography>
            <Typography variant="h4" sx={{ mt: 1 }}>
              {invite?.actionType === 'password_reset' ? 'Reset your password' : 'Activate your account'}
            </Typography>
            {invite?.email ? (
              <Typography color="text.secondary" sx={{ mt: 1 }}>
                Continue as {invite.email}
              </Typography>
            ) : null}
          </Box>

          {status === 'loading' ? <LinearProgress /> : null}
          {message ? <Alert severity="error">{message}</Alert> : null}
          {status === 'expired' ? (
            <Alert severity="warning">{expiredMessage(invite)}</Alert>
          ) : null}
          {status === 'success' ? (
            <Alert severity="success">
              Password updated. You can now return to the sign-in page.
            </Alert>
          ) : null}

          {status !== 'success' && status !== 'expired' ? (
            <Box component="form" onSubmit={handleSubmit} noValidate>
              <Stack spacing={2}>
                {requiresName ? (
                  <>
                    <TextField
                      label="First name"
                      fullWidth
                      value={firstName}
                      onChange={(event) => setFirstName(event.target.value)}
                      disabled={!invite || status === 'loading'}
                    />
                    <TextField
                      label="Last name"
                      fullWidth
                      value={lastName}
                      onChange={(event) => setLastName(event.target.value)}
                      disabled={!invite || status === 'loading'}
                    />
                  </>
                ) : null}
                <TextField
                  label="New password"
                  type="password"
                  fullWidth
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  disabled={!invite || status === 'loading'}
                />
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={
                    !invite
                    || status === 'loading'
                    || password.trim().length < 8
                    || (requiresName
                      && (firstName.trim().length === 0 || lastName.trim().length === 0))
                  }
                >
                  {submitLabel}
                </Button>
              </Stack>
            </Box>
          ) : null}

          <Button variant="text" onClick={() => { window.location.href = '/'; }}>
            Back to sign in
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
};

export default AcceptInvitePage;
